/**
 * Two audiences, two dictionaries (redesign doc §6).
 *
 * The UI speaks the schema's language to both — `canary`, `extractor version`, `escalated
 * attempt`, `healing`. Those are the real domain terms and the operator is technical, so the
 * OPERATOR column is deliberately left exactly as-is: translating for them would be
 * condescending and lossy. The customer signed in with Google and did not read the schema.
 *
 * Rules: active voice, sentence case, and the button's verb survives into its confirmation
 * ("Approve fix" -> "Fix approved").
 */

export interface Words {
  /** An extractor version awaiting a human decision. */
  canary: string;
  /** extractor_versions row. */
  version: string;
  /** A run currently executing. */
  running: string;
  /** A scraper paused after healing was exhausted. */
  paused: string;
  /** A healing attempt. */
  heal: string;
  /** Past tense — this thing was repaired by AI. */
  healed: string;
  /** The scar on the ribbon. */
  seam: string;
}

const OPERATOR: Words = {
  canary: "canary",
  version: "version",
  running: "running",
  paused: "paused",
  heal: "heal",
  healed: "healed",
  seam: "seam",
};

const CUSTOMER: Words = {
  canary: "fix waiting",
  version: "setup",
  running: "collecting",
  paused: "needs a look",
  heal: "repair",
  healed: "repaired",
  seam: "repair",
};

/** Pick the dictionary for who is looking. */
export function words(operator: boolean): Words {
  return operator ? OPERATOR : CUSTOMER;
}

/**
 * Label an extractor version for display: operators count versions ("v3"), customers read
 * setups ("setup 3"). Same number either way — only the noun changes.
 */
export function versionLabel(operator: boolean, n: number): string {
  return operator ? `v${n}` : `setup ${n}`;
}

/**
 * Render a raw DB status for display. Operators see the real value — it is the schema's word
 * and they debug against it. Customers see only the ones that are genuinely opaque; `success`,
 * `failed` and `active` already read fine to anyone, so they are left alone rather than
 * translated for the sake of it.
 */
export function statusText(operator: boolean, status: string): string {
  if (operator) return status;
  const w = CUSTOMER;
  if (status === "running") return w.running;
  if (status === "paused") return w.paused;
  if (status === "canary") return w.canary;
  if (status === "archived") return "replaced";
  return status;
}
